import { useDotButton } from "@/components/Gallery/hooks/useDotButton.ts";
import { useLightbox } from "@/components/Gallery/hooks/useLightbox.ts";
import { Image } from "@/helpers/types/types.ts";
import useEmblaCarousel, { EmblaCarouselType, EmblaOptionsType } from "embla-carousel-react";
import { useCallback, useEffect, useState } from "react";

interface UseGalleryCarouselOptions {
  slides: Image[];
  options?: EmblaOptionsType;
  keyboardNav?: boolean;
}

export const useGalleryCarousel = ({ slides, options, keyboardNav }: UseGalleryCarouselOptions) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const lightbox = useLightbox(slides);

  const [gallery, emblaMainApi] = useEmblaCarousel(options);
  const [galleryThumbnails, emblaThumbsApi] = useEmblaCarousel({
    containScroll: "keepSnaps",
    dragFree: true,
  });

  const { scrollSnaps, onDotButtonClick } = useDotButton(emblaMainApi, selectedIndex, setSelectedIndex);

  const scrollPrev = useCallback(() => emblaMainApi?.scrollPrev(), [emblaMainApi]);
  const scrollNext = useCallback(() => emblaMainApi?.scrollNext(), [emblaMainApi]);

  const onThumbClick = useCallback(
    (index: number) => {
      if (!emblaMainApi || !emblaThumbsApi) return;
      emblaMainApi.scrollTo(index);
    },
    [emblaMainApi, emblaThumbsApi],
  );

  const onSelect = useCallback(
    (api: EmblaCarouselType) => {
      if (!api || !emblaThumbsApi) return;
      setSelectedIndex(api.selectedScrollSnap());
      emblaThumbsApi.scrollTo(api.selectedScrollSnap());
    },
    [emblaThumbsApi],
  );

  useEffect(() => {
    if (!emblaMainApi) return;
    onSelect(emblaMainApi);
    emblaMainApi.on("select", onSelect);
    emblaMainApi.on("reInit", onSelect);
  }, [emblaMainApi, onSelect]);

  useEffect(() => {
    if (!keyboardNav || !emblaMainApi || lightbox.isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") emblaMainApi.scrollPrev();
      if (e.key === "ArrowRight") emblaMainApi.scrollNext();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [keyboardNav, emblaMainApi, lightbox.isOpen]);

  return {
    selectedIndex,
    lightbox,
    gallery,
    galleryThumbnails,
    scrollSnaps,
    onDotButtonClick,
    scrollPrev,
    scrollNext,
    onThumbClick,
    hasMultipleSlides: slides.length > 1,
  };
};
